'use client';

import { useEffect, useState, type MouseEvent } from 'react';
import { useFormikContext } from 'formik';
import { useAlertBanner, useUserSession } from '@libs/common-next';

import { ButtonGroup, Button, Heading, Grid, LoadingIndicator } from '@petsmart-ui/sparky';

import './BillingAddress.scss';

import { BillingAddressForm } from './BillingAddressForm';
import { BillingAddressSelector } from '../BillingAddressSelector';
import type { BillingAddressProps } from './types';
import { DEFAULT_ADDRESS_FORM_STATE } from '../PaymentForm/constants';
import type { PaymentFormInterface } from '../PaymentForm/types';

export function BillingAddress({ onCancel, handleErrorBanner, isLoading }: Readonly<BillingAddressProps>) {
    const [showForm, setShowForm] = useState<boolean>(false);

    const { userData } = useUserSession();
    const { addresses } = { ...userData };

    const { setAlert } = useAlertBanner();

    const { setValues, values, errors, submitForm } = useFormikContext<PaymentFormInterface>();

    const addressListLoading = addresses === undefined;

    useEffect(() => {
        if (addresses === null || addresses?.length === 0) {
            setShowForm(true);
        }
    }, [addresses]);

    const scrollToBillingSection = () => {
        const title = document.querySelector<HTMLElement>('[data-testid="billing-address"]');
        if (title) {
            title.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleAddNewAddressClick = () => {
        setValues({
            ...values,
            address: DEFAULT_ADDRESS_FORM_STATE,
            firstName: '',
            lastName: '',
            phone: '',
            addressType: 'form',
        });
        setShowForm(true);
        requestAnimationFrame(() => {
            scrollToBillingSection();
        });
    };

    const handleCancelClick = () => {
        setAlert(null);

        if (addresses?.length && showForm) {
            setValues({
                ...values,
                addressType: 'id',
                address: undefined,
                firstName: undefined,
                lastName: undefined,
                phone: undefined,
            });
            setShowForm(false);
        } else {
            onCancel();
        }
    };

    const handleSubmitClick = (e: MouseEvent<Element>) => {
        e.preventDefault();

        handleErrorBanner(errors);
        submitForm();
    };

    return (
        <div className="billing-address" data-testid="billing-address">
            <Heading tagName="h6" size="body-lg-bold" data-testid="billing-address-title">
                Billing address
            </Heading>

            <Grid>
                {addressListLoading && (
                    <Grid.Item>
                        <LoadingIndicator iconSize="4xl" data-testid="loading-indicator" />
                    </Grid.Item>
                )}

                {addresses?.length !== 0 && !showForm && (
                    <Grid.Item className="address-selector-item">
                        <BillingAddressSelector billingAddresses={addresses} />
                        <Button
                            data-testid="add-address-btn"
                            type="button"
                            text="Add new address"
                            variant="link"
                            iconName="plus"
                            iconPosition="before"
                            onClick={handleAddNewAddressClick}
                        />
                    </Grid.Item>
                )}

                {showForm && (
                    <Grid.Item>
                        <BillingAddressForm />
                    </Grid.Item>
                )}

                <Grid.Item>
                    <ButtonGroup
                        behavior="responsive"
                        className="billing-address__button-group"
                        data-testid="billing-address-button-group"
                    >
                        <Button
                            disabled={isLoading}
                            text="Cancel"
                            size="lg"
                            type="button"
                            fullWidth
                            onClick={handleCancelClick}
                        />
                        <Button
                            loading={isLoading}
                            disabled={isLoading}
                            text="Save"
                            size="lg"
                            type="submit"
                            variant="primary"
                            className="billing-address__save-cta"
                            fullWidth
                            onClick={e => {
                                handleSubmitClick(e);
                            }}
                        />
                    </ButtonGroup>
                </Grid.Item>
            </Grid>
        </div>
    );
}
